class ProductThumbVisibilityController {
  constructor() {
    this.section = document.querySelector('[data-section-type="product"]');
    this.thumbSelector = '.product__thumb-item';
    this.dotSelector = '.flickity-page-dots .dot';
    this.variants = [];
    this.groupStarts = [];
    this.lastRealVariant = null;
    this.syncTimer = null;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    if (!this.section) return;
    this.loadVariants();
    this.buildGroupStarts();
    this.bindEvents();
    this.syncFromCurrentSelection();
  }

  handleize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+/, '')
      .replace(/-+$/, '');
  }

  getVariantHandle(variant) {
    if (!variant) return '';
    return this.handleize(variant.title);
  }

  hasSku(variant) {
    return !!(variant && typeof variant.sku === 'string' && variant.sku.trim() !== '');
  }

  getSiblingSkuVariant(variant) {
    if (!variant || !Array.isArray(this.variants) || !this.variants.length) return null;

    const skuVariants = this.variants.filter((item) => this.hasSku(item));
    if (!skuVariants.length) return null;

    const optionKeys = ['option1', 'option2', 'option3'];
    const scored = skuVariants
      .map((candidate) => {
        let score = 0;

        if (variant.option1 && candidate.option1 === variant.option1) score += 16;
        if (variant.option2 && candidate.option2 === variant.option2) score += 8;
        if (variant.option3 && candidate.option3 === variant.option3) score += 2;
        if (candidate.available) score += 1;

        return { candidate, score };
      })
      .filter((entry) => {
        // Keep candidates in the same product branch.
        // Important: do not require option2 (metalware), because fake option2
        // values (no SKU) must still resolve to the real sibling in the same color/model.
        return !variant.option1 || entry.candidate.option1 === variant.option1;
      })
      .sort((a, b) => b.score - a.score);

    if (!scored.length) return null;
    return scored[0].candidate || null;
  }

  getVariantStartPosition(variant) {
    if (!variant) return null;
    const position = variant.featured_media && variant.featured_media.position;
    const parsed = parseInt(position, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  loadVariants() {
    const variantsJsonEl = this.section.querySelector('[data-variant-json]');
    if (!variantsJsonEl) return;

    try {
      this.variants = JSON.parse(variantsJsonEl.textContent || variantsJsonEl.innerHTML || '[]');
    } catch (e) {
      this.variants = [];
    }
  }

  getSelectedOptionsFromInputs() {
    const selected = {};
    const inputs = this.section.querySelectorAll('[data-variant-input]');

    inputs.forEach((input) => {
      const index = input.dataset.index;
      if (!index) return;
      const type = input.getAttribute('type');

      if (type === 'radio' || type === 'checkbox') {
        if (input.checked) {
          selected[index] = input.value;
        }
      } else if (input.value) {
        selected[index] = input.value;
      }
    });

    return selected;
  }

  resolveVariantFromInputs() {
    if (!Array.isArray(this.variants) || !this.variants.length) return null;

    const selected = this.getSelectedOptionsFromInputs();
    const optionKeys = ['option1', 'option2', 'option3'];

    const exactMatches = this.variants.filter((variant) => {
      return optionKeys.every((key) => {
        if (!selected[key]) return true;
        return variant[key] === selected[key];
      });
    });

    if (!exactMatches.length) return null;

    // For gallery grouping we must follow the real selected branch first,
    // including real OOS variants, instead of preferring "available".
    const ranked = exactMatches.filter((variant) => this.hasSku(variant));

    if (ranked.length) return ranked[0];
    return exactMatches[0];
  }

  buildGroupStarts() {
    const starts = new Set();
    this.variants.forEach((variant) => {
      const start = this.getVariantStartPosition(variant);
      if (start) starts.add(start);
    });
    this.groupStarts = Array.from(starts).sort((a, b) => a - b);
  }

  getNextGroupStart(startPosition) {
    for (let i = 0; i < this.groupStarts.length; i += 1) {
      if (this.groupStarts[i] > startPosition) {
        return this.groupStarts[i];
      }
    }
    return null;
  }

  getThumbPosition(thumb) {
    const indexAttr = thumb.getAttribute('data-index');
    const index = parseInt(indexAttr, 10);
    if (!Number.isFinite(index)) return null;
    return index + 1;
  }

  showThumbRange(startPosition, endPositionExclusive) {
    const thumbs = this.section.querySelectorAll(this.thumbSelector);
    let firstVisibleIndex = null;

    thumbs.forEach((thumb) => {
      const position = this.getThumbPosition(thumb);
      let isVisible = true;
      if (position) {
        isVisible = position >= startPosition && (endPositionExclusive === null || position < endPositionExclusive);
      }

      thumb.style.display = isVisible ? '' : 'none';
      if (isVisible && firstVisibleIndex === null) {
        firstVisibleIndex = thumb.getAttribute('data-index');
      }
    });

    this.syncDots();
    this.ensureActiveSlideVisible(firstVisibleIndex);
  }

  hideAllThumbs() {
    this.section.querySelectorAll(this.thumbSelector).forEach((thumb) => {
      thumb.style.display = 'none';
    });
    this.syncDots();
  }

  showAllThumbs() {
    this.section.querySelectorAll(this.thumbSelector).forEach((thumb) => {
      thumb.style.display = '';
    });
    this.syncDots();
  }

  syncDots() {
    const thumbs = this.section.querySelectorAll(this.thumbSelector);
    const dots = this.section.querySelectorAll(this.dotSelector);

    thumbs.forEach((thumb, index) => {
      const dot = dots[index];
      if (!dot) return;
      dot.style.display = thumb.style.display === 'none' ? 'none' : '';
    });
  }

  ensureActiveSlideVisible(firstVisibleIndexAttr) {
    const sliderEl = this.section.querySelector('[data-product-photos]');
    if (!sliderEl || !window.Flickity) return;

    const flkty = Flickity.data(sliderEl);
    if (!flkty) return;

    const selectedElement = flkty.selectedElement;
    const selectedIndexAttr = selectedElement ? selectedElement.getAttribute('data-index') : null;
    const selectedThumb = selectedIndexAttr !== null
      ? this.section.querySelector(`${this.thumbSelector}[data-index="${selectedIndexAttr}"]`)
      : null;
    const currentHidden = !selectedThumb || selectedThumb.style.display === 'none';

    if (currentHidden && firstVisibleIndexAttr !== null) {
      const targetIndex = parseInt(firstVisibleIndexAttr, 10);
      if (Number.isFinite(targetIndex)) {
        flkty.select(targetIndex, false, true);
      }
    }
  }

  showThumbsForVariantHandle(variantHandle) {
    const thumbs = this.section.querySelectorAll(this.thumbSelector);
    let firstVisibleIndex = null;
    let visibleCount = 0;

    thumbs.forEach((thumb) => {
      const thumbHandle = this.handleize(thumb.getAttribute('data-variant'));
      const isVisible = thumbHandle === variantHandle;
      thumb.style.display = isVisible ? '' : 'none';

      if (isVisible) {
        visibleCount += 1;
        if (firstVisibleIndex === null) {
          firstVisibleIndex = thumb.getAttribute('data-index');
        }
      }
    });

    this.syncDots();
    this.ensureActiveSlideVisible(firstVisibleIndex);
    return visibleCount > 0;
  }

  syncByVariant(variant) {
    // During variant transitions we can briefly receive a fake/null state.
    // Prefer a real SKU sibling in the same branch before using stale state.
    if (!variant || !this.hasSku(variant)) {
      const siblingVariant = this.getSiblingSkuVariant(variant);
      if (siblingVariant) {
        variant = siblingVariant;
        this.lastRealVariant = siblingVariant;
      } else if (this.lastRealVariant) {
        variant = this.lastRealVariant;
      } else {
        this.hideAllThumbs();
        return;
      }
    } else {
      this.lastRealVariant = variant;
    }

    const variantHandle = this.getVariantHandle(variant);
    if (variantHandle && this.showThumbsForVariantHandle(variantHandle)) {
      return;
    }

    const start = this.getVariantStartPosition(variant);
    if (!start) {
      // Safety fallback when product media positions are missing.
      this.showAllThumbs();
      return;
    }

    const nextStart = this.getNextGroupStart(start);
    this.showThumbRange(start, nextStart);
  }

  syncFromCurrentSelection() {
    const variantIdInput = this.section.querySelector('[data-product-select]');
    if (!variantIdInput || !this.variants.length) return;

    const currentVariantId = parseInt(variantIdInput.value, 10);
    const currentVariant = this.variants.find((variant) => variant.id === currentVariantId) || null;
    this.syncByVariant(currentVariant);
  }

  getCurrentSelectedVariant() {
    const variantIdInput = this.section.querySelector('[data-product-select]');
    if (!variantIdInput || !this.variants.length) return null;

    const currentVariantId = parseInt(variantIdInput.value, 10);
    if (!Number.isFinite(currentVariantId)) return null;

    return this.variants.find((variant) => variant.id === currentVariantId) || null;
  }

  scheduleSync(variantFromEvent) {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
    }

    this.syncTimer = setTimeout(() => {
      const settledVariant =
        this.getCurrentSelectedVariant() ||
        this.resolveVariantFromInputs() ||
        variantFromEvent ||
        null;
      this.syncByVariant(settledVariant);
    }, 30);
  }

  bindEvents() {
    document.addEventListener('variant:change', (event) => {
      const detail = event && event.detail ? event.detail : null;
      if (!detail || !Object.prototype.hasOwnProperty.call(detail, 'variant')) return;
      this.scheduleSync(detail.variant || null);
    });

    document.addEventListener('change', (event) => {
      const target = event.target;
      if (!target || !target.matches) return;
      if (target.matches('[data-product-select], [data-variant-input]')) {
        this.scheduleSync(this.getCurrentSelectedVariant());
      }
    });
  }
}

new ProductThumbVisibilityController();
